import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, apiClient } from "@/lib/api";
import { Mail, X, ExternalLink, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function GmailPermissionBanner() {
  const { user } = useAuth();
  const [showBanner, setShowBanner] = useState(false);
  const [emailProvider, setEmailProvider] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  
  useEffect(() => {
    // Check if we need to show Gmail permission banner
    const needsGmailAuth = localStorage.getItem("viraly_needs_gmail_auth") === "true";
    const provider = localStorage.getItem("viraly_email_provider");
    const wasDismissed = localStorage.getItem("viraly_gmail_banner_dismissed") === "true";
    
    if (needsGmailAuth && !wasDismissed) {
      setShowBanner(true);
      setEmailProvider(provider);
    } else if (provider && !wasDismissed) {
      // For non-Google providers, show info banner
      setEmailProvider(provider);
      if (provider === "microsoft" || provider === "other") {
        setShowBanner(true);
      }
    }
    
    // Listen for Gmail OAuth success
    const handleMessage = (event) => {
      if (event.data.type === 'google_oauth_success') {
        toast.success(`Email connected: ${event.data.email}`);
        localStorage.removeItem("viraly_needs_gmail_auth");
        setShowBanner(false);
        setConnecting(false);
      } else if (event.data.type === 'google_oauth_error') {
        toast.error("Failed to connect email. Please try again.");
        setConnecting(false);
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  
  const handleConnectGmail = async () => {
    setConnecting(true);
    try {
      const response = await apiClient.get('/oauth/google/connect');
      if (response.data.auth_url) {
        // Open popup for OAuth
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        const popup = window.open(
          response.data.auth_url,
          'gmail_oauth',
          `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
        );
        
        // Check if popup was blocked
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
          toast.error("Popup blocked. Please allow popups for this site.");
          setConnecting(false);
        }
      }
    } catch (error) {
      console.error("Gmail connect error:", error);
      toast.error("Failed to initiate Gmail connection");
      setConnecting(false);
    }
  };
  
  const handleDismiss = () => {
    setDismissed(true);
    setShowBanner(false);
    localStorage.setItem("viraly_gmail_banner_dismissed", "true");
    localStorage.removeItem("viraly_needs_gmail_auth");
  };
  
  const handleLater = () => {
    setShowBanner(false);
    // Don't set dismissed flag, so it will show again next session
  };
  
  if (!showBanner || dismissed) return null;
  
  // Different content based on email provider
  if (emailProvider === "microsoft") {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="mb-6 rounded-xl bg-gradient-to-r from-blue-500/20 to-blue-600/20 border border-blue-500/30 p-4"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-white">Microsoft 365 Email - Coming Soon</h3>
                <p className="text-sm text-viraly-muted mt-1">
                  We detected you're using Microsoft 365 for email. Email automation features will be available soon!
                  For now, you can still create and schedule content.
                </p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-viraly-muted hover:text-white transition-colors p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }
  
  if (emailProvider === "other" || emailProvider === "unknown") {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="mb-6 rounded-xl bg-gradient-to-r from-gray-500/20 to-gray-600/20 border border-gray-500/30 p-4"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-500/20 flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-gray-400" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-white">Email Provider Not Supported</h3>
                <p className="text-sm text-viraly-muted mt-1">
                  Your email provider isn't currently supported for automated email sending.
                  We support Gmail and Google Workspace, with Microsoft 365 coming soon.
                  You can still create and schedule content!
                </p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-viraly-muted hover:text-white transition-colors p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }
  
  // Google email - prompt for Gmail permissions
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="mb-6 rounded-xl bg-gradient-to-r from-viraly-orange/20 to-yellow-500/20 border border-viraly-orange/30 p-4"
        data-testid="gmail-permission-banner"
      >
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-viraly-orange/20 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-viraly-orange" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-white">Enable Email Automation</h3>
              <p className="text-sm text-viraly-muted mt-1">
                Grant permission to send emails from your Google account ({user?.email}).
                This lets Viraly send welcome emails, notifications, and scheduled messages on your behalf.
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 ml-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLater}
              className="text-viraly-muted hover:text-white"
            >
              Later
            </Button>
            <Button
              onClick={handleConnectGmail}
              disabled={connecting}
              className="bg-viraly-gradient hover:opacity-90 text-white gap-2"
              size="sm"
              data-testid="connect-gmail-btn"
            >
              {connecting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Connect Gmail
                </>
              )}
            </Button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
